"use client";

import { useState, useTransition } from "react";
import { toast } from "sonner";
import { reportFeedItem } from "@/actions/user-report";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import type { FeedShareEntity } from "@/db/schema/feed-share";
import {
  USER_REPORT_REASON,
  type UserReportReason,
} from "@/db/schema/user-report";
import { cn } from "@/lib/utils";

type ReportFeedItemDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetUserId: string;
  targetLabel: string;
  entityType: FeedShareEntity;
  entityId: number;
};

const REASON_LABELS: Record<UserReportReason, string> = {
  harassment: "Harassment",
  spam: "Spam",
  safety: "Safety concern",
  inappropriate_content: "Inappropriate content",
  fake_profile: "Fake profile",
  other: "Other",
};

export function ReportFeedItemDialog({
  open,
  onOpenChange,
  targetUserId,
  targetLabel,
  entityType,
  entityId,
}: ReportFeedItemDialogProps) {
  const [reason, setReason] = useState<UserReportReason>("other");
  const [details, setDetails] = useState("");
  const [isPending, startTransition] = useTransition();

  const handleSubmit = () => {
    startTransition(async () => {
      const result = await reportFeedItem({
        targetUserId,
        entityType,
        entityId,
        reason,
        details,
      });

      if (!result.ok) {
        toast.error(result.message ?? "Unable to submit report");
        return;
      }

      toast.success(
        result.data.duplicate ? "Report already open" : "Report submitted",
      );
      setDetails("");
      setReason("other");
      onOpenChange(false);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report {targetLabel}</DialogTitle>
          <DialogDescription>
            Reports go to platform admins for review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {USER_REPORT_REASON.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setReason(option)}
                className={cn(
                  "min-h-10 rounded-lg border border-border px-3 py-2 text-left text-sm font-medium transition",
                  reason === option
                    ? "border-brand bg-brand/10 text-brand"
                    : "bg-surface text-text-secondary hover:bg-surface-alt",
                )}
                disabled={isPending}
              >
                {REASON_LABELS[option]}
              </button>
            ))}
          </div>

          <Textarea
            value={details}
            onChange={(event) => setDetails(event.target.value)}
            placeholder="Add details for moderators..."
            maxLength={2000}
            rows={4}
            disabled={isPending}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isPending}>
            {isPending ? "Submitting..." : "Submit report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
